import * as React from 'react';
import {
  Alert,
  Button,
  EmptyState,
  KeyValueRow,
  Loading,
  MobileHeader,
  MobileScreen,
  PhotoStrip,
  Stack,
  Text,
  StatusChip,
} from '@relay-towing/ui';
import { useParams } from 'react-router-dom';
import type { ReportAnswer } from '../../api/truckInspections';
import { useInspectionReport } from '../../hooks/useTruckInspections';
import { answerValue, statusLabel } from '../../lib/inspections';
import { PhotoViewer } from '../shell/PhotoViewer';
import { useBack } from '../shell/useBack';
import { dayAndTime } from '../status';

/** A report's answers under their checklist sections, in the order asked. */
function bySection(answers: ReportAnswer[]) {
  const sections: Array<[string, ReportAnswer[]]> = [];
  for (const answer of answers) {
    const key = answer.itemSection || 'Checklist';
    const found = sections.find(([name]) => name === key);
    if (found) found[1].push(answer);
    else sections.push([key, [answer]]);
  }
  return sections;
}

const photosOf = (urls: string[] | null | undefined) =>
  (urls ?? []).map((url) => ({ id: url, src: url }));

/**
 * One filed inspection, to read. No Figma frame exists for it: the layout
 * is inferred, and a designer's frame replaces it. It follows the old
 * report screen (screens/inspections/InspectionReportScreen.tsx) laid out
 * as the invoice and impound screens are: the facts, how it came out, each
 * answer under its section with its photos, then the notes, the photos of
 * what was checked and who signed it off.
 *
 * At /inspections/report/:id, where a run lands once its report is
 * filed (InspectionRunScreen). The server sends only a report the member
 * may see: their own, or any with "View all inspections".
 */
export function InspectionDetailScreen() {
  const { id = '' } = useParams<{ id: string }>();
  const back = useBack('/inspections');
  const { data: report, isPending, isError, refetch } = useInspectionReport(id);
  const [viewing, setViewing] = React.useState<{
    src: string;
    title: string;
  } | null>(null);
  const open = (title: string) => (src: string) => setViewing({ src, title });

  const subject = report ? report.subjectLabel : 'Inspection';
  const header = (
    <MobileHeader
      onBack={back}
      backLabel="Back to inspections"
      title={subject}
    />
  );

  if (isPending)
    return <Loading label="Loading the inspection" fill="screen" />;
  if (isError || !report)
    return (
      <MobileScreen header={header}>
        <EmptyState
          title="This inspection is not available"
          description="Check your signal and try again. If it still does not open, it is not one your role can see."
          action={
            <Button variant="outline" onClick={() => void refetch()}>
              Try again
            </Button>
          }
        />
      </MobileScreen>
    );

  // The tone follows the report's status, not the count of failed answers:
  // a question can be set so that failing it is written down without
  // failing the truck, and this has to agree with the status the office sees.
  const issues = report.status === 'ISSUES_FOUND';
  const failed = report.answers.filter((a) => a.status === 'FAIL').length;
  const flagged = `${failed} item${failed === 1 ? '' : 's'} flagged`;

  return (
    <MobileScreen header={header}>
      <Stack gap={6}>
        <Stack gap={0} divided>
          <KeyValueRow
            label="Result"
            value={
              <StatusChip
                label={statusLabel(report.status)}
                tone={issues ? 'destructive' : 'success'}
              />
            }
          />
          <KeyValueRow label="Checklist" value={report.templateName} />
          <KeyValueRow
            label="Filed"
            value={report.submittedAt ? dayAndTime(report.submittedAt) : null}
            hideEmpty
          />
          <KeyValueRow
            label="Odometer"
            value={report.odometer?.toLocaleString()}
            hideEmpty
          />
          {/* Only the label: a pair of coordinates tells nobody anything. */}
          <KeyValueRow label="Where" value={report.locationLabel} hideEmpty />
        </Stack>
        <Alert tone={issues ? 'destructive' : 'success'}>
          {issues
            ? flagged
            : failed === 0
              ? 'Everything passed'
              : `Passed. ${flagged}`}
        </Alert>
        {bySection(report.answers).map(([section, answers]) => (
          <Stack key={section} gap={2} as="section">
            <Text variant="overline" tone="muted" as="h2">
              {section}
            </Text>
            <Stack gap={0} divided>
              {answers.map((answer) => (
                <Stack key={answer.id} gap={2}>
                  <KeyValueRow
                    label={answer.itemLabel}
                    value={answerValue(answer)}
                    tone={
                      answer.status === 'FAIL'
                        ? 'destructive'
                        : answer.status === 'PASS'
                          ? 'success'
                          : 'default'
                    }
                  />
                  {answer.note ? (
                    <Text variant="body-sm" tone="muted">
                      {answer.note}
                    </Text>
                  ) : null}
                  {answer.photoUrls.length ? (
                    <PhotoStrip
                      photos={photosOf(answer.photoUrls)}
                      onOpen={open(answer.itemLabel)}
                    />
                  ) : null}
                </Stack>
              ))}
            </Stack>
          </Stack>
        ))}
        {report.notes ? (
          <Stack gap={2} as="section">
            <Text variant="overline" tone="muted" as="h2">
              Notes
            </Text>
            <Text variant="body">{report.notes}</Text>
          </Stack>
        ) : null}
        {report.photoUrls?.length ? (
          <Stack gap={2} as="section">
            <Text variant="overline" tone="muted" as="h2">
              Photos
            </Text>
            <PhotoStrip
              photos={photosOf(report.photoUrls)}
              onOpen={open('Photo')}
            />
          </Stack>
        ) : null}
        {report.signedName || report.signatureUrl ? (
          <Stack gap={2} as="section">
            <Text variant="overline" tone="muted" as="h2">
              Signed off
            </Text>
            <KeyValueRow
              label="Signed by"
              value={report.signedName}
              hideEmpty
            />
            {report.signatureUrl ? (
              <Button
                variant="outline"
                onClick={() => open('Signature')(report.signatureUrl!)}
              >
                See the signature
              </Button>
            ) : null}
          </Stack>
        ) : null}
      </Stack>
      <PhotoViewer
        src={viewing?.src ?? null}
        title={viewing?.title}
        alt={viewing?.title ?? 'Photo'}
        onClose={() => setViewing(null)}
      />
    </MobileScreen>
  );
}
